import {cursor, LimitOffsetPagination} from '@/api/paginatedResults';
import {
  type SelectedCustomer,
  type Customer,
  type UserRelation,
  type ContactType,
  type Contact,
  apiGetUsers,
  apiGetCustomer,
  apiGetCustomers,
  apiDeleteUserRelation,
  apiPostContact,
  apiGetContacts,
  apiDeleteContact,
  apiGetContactTypes,
} from '@/api/apolloPlatform/customers';
import {useLocalStorage} from '@/composables/localStorage';

export const useCustomersStore = defineStore('customers', () => {
  const selectedCustomer = ref<SelectedCustomer | null>(null);
  const customers = ref<Customer[] | null>(null);
  const userRelations = ref<UserRelation[] | null>(null);
  const contactTypes = ref<ContactType[] | null>(null);
  const contacts = ref<Contact[] | null>(null);
  const demoCustomers = ref<DemoCustomer[] | null>(null);

  // If permission on the customer changes, then we need to reload the page to
  // apply this. This boolean tracks whether this is needed so it can be done at
  // an opportune time. (e.g., when closing the settings modal).
  const customerRefreshNeeded = ref(false);

  const getCustomers = computed(() => customers.value ?? []);
  const getContactTypes = computed(() => contactTypes.value ?? []);
  const getContacts = computed(() => contacts.value ?? []);
  const getDemoCustomers = computed(() => demoCustomers.value ?? []);

  const setSelectedCustomer = (value: SelectedCustomer) => {
    const {setItem} = useLocalStorage();
    setItem('selectedCustomer', value);
    selectedCustomer.value = value;
  };

  const setCustomers = (value: Customer[]) => (customers.value = value);

  const readCustomers = async (search = ''): Promise<Customer[]> => {
    const {getItem} = useLocalStorage();
    const localStorageCustomers: Customer[] | null = getItem('customers');
    if (localStorageCustomers) customers.value = localStorageCustomers;
    try {
      // Get the first cursor data only.
      const response = await apiGetCustomers(undefined, undefined, search);
      const data = cursor<LimitOffsetPagination<Customer[]>>(response.data);
      /**
       * If a search argement was provided, get all search cursor results.
       * Don't setCustomers as search results will remove previously cached customers.
       */
      if (search.length > 0) {
        // Get the next cursor data
        const aggregatedCustomerResults: Customer[] =
          data.next !== null ? await apiGetPaginatedResults(apiGetCustomers, data.next) : [];
        // Return both the first cursor & rest of the aggregated cursors.
        return [...data.results, ...aggregatedCustomerResults];
      } else {
        customers.value = data.results;
        return data.results; // Return the first cursor only.
      }
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const readCustomerById = async (id: number): Promise<SelectedCustomer> => {
    const {getItem} = useLocalStorage();
    const localStorageSelectedCustomer: SelectedCustomer | null = getItem('selectedCustomer');
    if (localStorageSelectedCustomer) setSelectedCustomer(localStorageSelectedCustomer);
    try {
      const {data} = await apiGetCustomer(id);
      setSelectedCustomer(data);
      return data;
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const readUserRelations = async (id: number) => {
    try {
      const {data} = await apiGetUsers(id);
      userRelations.value = data;
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const deleteUserRelation = async (customerId: string | number, userId: string | number) => {
    try {
      await apiDeleteUserRelation(customerId, userId);
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const createContact = async (contact: ContactCreate) => {
    try {
      const {data} = await apiPostContact(contact);
      return data;
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const readContacts = async (customerId: number) => {
    try {
      const {data} = await apiGetContacts(customerId); // This endpoint uses cursor pagination
      contacts.value = data;
      selectedCustomer.value!.contacts = data;
      return data;
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const deleteContact = async (customerId: number, contactId: number) => {
    try {
      await apiDeleteContact(customerId, contactId);
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const readContactTypes = async () => {
    try {
      const {data} = await apiGetContactTypes(); // This endpoint uses cursor pagination
      // Get the next cursor data
      const aggregatedResults: ContactType[] =
        data.next !== null ? await apiGetPaginatedResults(apiGetContactTypes, data.next) : [];
      // Return both the first cursor & rest of the aggregated cursors.
      const results: ContactType[] = [...data.results, ...aggregatedResults];
      contactTypes.value = results;
      return results;
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const updateDemoCustomerInStore = (updated: DemoCustomer) => {
    if (demoCustomers.value) {
      const idx = demoCustomers.value.findIndex((c) => c.id === updated.id);
      if (idx !== -1) demoCustomers.value[idx] = updated;
    }

    customers.value ??= [];
    const idx = customers.value.findIndex((c) => c.id === updated.id);
    if (!updated.active) {
      if (idx !== -1) customers.value.splice(idx, 1);
    } else if (idx !== -1) {
      customers.value[idx].name = updated.name;
    } else {
      customers.value.unshift({
        id: updated.id,
        name: updated.name,
        debtor_number: updated.debtor_number,
        active: updated.active,
        url: '',
        class_name: 'Customer',
      });
    }
  };

  const readDemoCustomers = async (): Promise<DemoCustomer[]> => {
    try {
      const {data} = await apiGetDemoCustomers();
      demoCustomers.value = data.results;
      return data.results;
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const createDemoCustomer = async (name: string, active_until: string): Promise<DemoCustomer> => {
    try {
      const {data} = await apiPostDemoCustomer(name, active_until);
      demoCustomers.value = [...(demoCustomers.value ?? []).filter((c) => c.id !== data.id), data];
      updateDemoCustomerInStore(data);
      return data;
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const updateDemoCustomer = async (
    id: number,
    name?: string,
    active_until?: string,
  ): Promise<DemoCustomer> => {
    try {
      const {data} = await apiPatchDemoCustomer(id, {name, active_until});
      updateDemoCustomerInStore(data);
      if (selectedCustomer.value?.customer.id === id) {
        selectedCustomer.value.customer.name = data.name;
      }
      return data;
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const enableDemoCustomer = async (id: number, active_until?: string): Promise<DemoCustomer> => {
    try {
      const {data} = await apiPostEnableDemoCustomer(id, active_until);
      updateDemoCustomerInStore(data);
      return data;
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const disableDemoCustomer = async (id: number): Promise<DemoCustomer> => {
    try {
      const {data} = await apiPostDisableDemoCustomer(id);
      updateDemoCustomerInStore(data);
      return data;
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };
  const selectDemoCustomer = async (id: number): Promise<SelectedCustomer> => {
    // this is called when the user clicks a customer in the demo customers table.
    // because we already have most of the demo customers: we can just use a cached one, the normal customer select flow will pick up the rest.
    try {
      const demo =
        demoCustomers.value?.find((c) => c.id === id) ?? (await apiGetDemoCustomer(id)).data;
      const selected: SelectedCustomer = {
        customer: {...demo, url: '', class_name: ''},
        contacts: [],
      };
      setSelectedCustomer(selected);
      return selected;
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  return {
    selectedCustomer,
    setCustomers,
    customers,
    userRelations,
    contactTypes,
    contacts,
    customerRefreshNeeded,
    getCustomers,
    getContactTypes,
    getContacts,
    setSelectedCustomer,
    readCustomers,
    readCustomerById,
    readUserRelations,
    deleteUserRelation,
    createContact,
    readContacts,
    deleteContact,
    readContactTypes,
    demoCustomers,
    getDemoCustomers,
    readDemoCustomers,
    createDemoCustomer,
    updateDemoCustomer,
    enableDemoCustomer,
    disableDemoCustomer,
    selectDemoCustomer,
  };
});
